import type {
  LookalikePersona,
  LookalikeRange,
  LookalikeSavviness,
  LookalikeSchema,
  WeightedValue,
} from '@repo/contracts';

/** One dimension per name here, in this order — `toFeatureVector`,
 *  `describeCluster` and any test asserting on a vector's shape all key off
 *  this list rather than a bare index, so adding a field later means editing
 *  one array, not three magic numbers. */
export const FEATURE_NAMES = [
  'age',
  'experienceYears',
  'location',
  'jobTitle',
  'companySize',
  'goal',
  'painPoint',
  'techSavviness',
  'brandLoyalty',
] as const;

export type FeatureName = (typeof FEATURE_NAMES)[number];

export const SAVVINESS_SCORE: Record<LookalikeSavviness, number> = {
  high: 1,
  low: 0,
  medium: 0.5,
};

function normalizeRange(range: LookalikeRange, value: number): number {
  const span = range.max - range.min;
  if (span <= 0) return 0.5;
  return Math.min(1, Math.max(0, (value - range.min) / span));
}

/**
 * Where a value sits in its own pool, as a fraction from 0 to 1.
 *
 * This is a *position*, not a magnitude: a pool's ordering carries no
 * guaranteed meaning (the model that wrote the schema didn't sort
 * `companySizes` by headcount), so this is only ever used as one of several
 * inputs into a clustering distance — never read back as "closer to 1 means
 * bigger." What it does capture correctly is togetherness: two lookalikes
 * that drew the *same* pool value always get the *same* position, so k-means
 * still groups them.
 */
function poolPosition(pool: readonly WeightedValue[], value: string): number {
  const index = pool.findIndex((entry) => entry.value === value);
  if (index < 0 || pool.length <= 1) return 0.5;
  return index / (pool.length - 1);
}

/** One lookalike, as the numeric vector `pca.ts` and `kmeans.ts` operate on —
 *  see `FEATURE_NAMES` for what each dimension is. Not standardised here;
 *  see `linalg.standardize` for that, applied across the whole population at
 *  once. */
export function toFeatureVector(
  schema: LookalikeSchema,
  lookalike: LookalikePersona,
): number[] {
  return [
    normalizeRange(schema.ageRange, lookalike.age),
    normalizeRange(schema.experienceYearsRange, lookalike.experienceYears),
    poolPosition(schema.locations, lookalike.location),
    poolPosition(schema.jobTitles, lookalike.jobTitle),
    poolPosition(schema.companySizes, lookalike.companySize),
    poolPosition(schema.goals, lookalike.goal),
    poolPosition(schema.painPoints, lookalike.painPoint),
    SAVVINESS_SCORE[lookalike.techSavviness],
    SAVVINESS_SCORE[lookalike.brandLoyalty],
  ];
}
